import { useNavigate } from '@tanstack/react-router';
import { FileText, Folder, ListChecks, PlayCircle } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { Dialog, EntityKey, Input, Spinner } from '../../components/ui';
import { useEnumLabels, useT } from '../../i18n';
import { useDebounced } from '../../lib/use-debounced';
import { useProjectSearch } from './api';

export interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectCode: string;
}

/** Project-scoped quick search (⌘K): test cases, checklists, runs and folders. */
export const CommandPalette = ({ open, onOpenChange, projectCode }: CommandPaletteProps) => {
  const navigate = useNavigate();
  const { t } = useT();
  const { runStatus } = useEnumLabels();
  const [term, setTerm] = useState('');
  const debounced = useDebounced(term, 250);
  const { data, isFetching } = useProjectSearch(projectCode, debounced);

  useEffect(() => {
    if (!open) setTerm('');
  }, [open]);

  const go = async (path: () => Promise<unknown>) => {
    onOpenChange(false);
    await path();
  };

  const hasResults =
    data !== undefined &&
    data.testCases.length + data.checklists.length + data.testRuns.length + data.folders.length > 0;

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={t('search.title', { project: projectCode })}
      description={t('search.hint')}
    >
      <Input
        value={term}
        onChange={(event) => setTerm(event.target.value)}
        placeholder={t('search.placeholder')}
        autoFocus
        aria-label={t('search.termLabel')}
      />

      <div className="mt-3 min-h-24">
        {debounced.trim().length === 0 ? (
          <p className="py-6 text-center text-[13px] text-ink-subtle">
            {t('search.prompt', {
              caseKey: `${projectCode}-1`,
              runKey: `${projectCode}-RUN-2`,
            })}
          </p>
        ) : isFetching && !data ? (
          <div className="flex justify-center py-6 text-ink-subtle">
            <Spinner />
          </div>
        ) : !hasResults ? (
          <p className="py-6 text-center text-[13px] text-ink-subtle">
            {t('search.nothing', { term: debounced })}
          </p>
        ) : (
          <div className="flex flex-col gap-3">
            <Group icon={<FileText className="size-3.5" />} title={t('search.groupCases')}>
              {data.testCases.map((hit) => (
                <Hit
                  key={hit.id}
                  entityKey={hit.key}
                  title={hit.title}
                  subtitle={hit.subtitle}
                  onSelect={() =>
                    void go(() =>
                      navigate({
                        to: '/projects/$projectCode/test-cases/$caseKey',
                        params: { projectCode, caseKey: hit.key },
                      }),
                    )
                  }
                />
              ))}
            </Group>

            <Group icon={<ListChecks className="size-3.5" />} title={t('search.groupChecklists')}>
              {data.checklists.map((hit) => (
                <Hit
                  key={hit.id}
                  entityKey={hit.key}
                  title={hit.title}
                  subtitle={
                    hit.itemCount === undefined
                      ? hit.subtitle
                      : t('checklists.countItems', { count: hit.itemCount })
                  }
                  onSelect={() =>
                    void go(() =>
                      navigate({
                        to: '/projects/$projectCode/checklists/$checklistKey',
                        params: { projectCode, checklistKey: hit.key },
                      }),
                    )
                  }
                />
              ))}
            </Group>

            <Group icon={<PlayCircle className="size-3.5" />} title={t('search.groupRuns')}>
              {data.testRuns.map((hit) => (
                <Hit
                  key={hit.id}
                  entityKey={hit.key}
                  title={hit.title}
                  subtitle={hit.subtitle ?? (hit.status ? runStatus(hit.status) : null)}
                  onSelect={() =>
                    void go(() =>
                      navigate({
                        to: '/projects/$projectCode/test-runs/$runKey',
                        params: { projectCode, runKey: hit.key },
                      }),
                    )
                  }
                />
              ))}
            </Group>

            <Group icon={<Folder className="size-3.5" />} title={t('search.groupFolders')}>
              {data.folders.map((hit) => (
                // A folder belongs to one list or the other, never to both.
                <Hit
                  key={hit.id}
                  title={hit.title}
                  subtitle={
                    hit.kind === 'checklist'
                      ? t('search.openInChecklists')
                      : t('search.openInCases')
                  }
                  onSelect={() =>
                    void go(() =>
                      navigate({
                        to:
                          hit.kind === 'checklist'
                            ? '/projects/$projectCode/checklists'
                            : '/projects/$projectCode/test-cases',
                        params: { projectCode },
                      }),
                    )
                  }
                />
              ))}
            </Group>
          </div>
        )}
      </div>
    </Dialog>
  );
};

const Group = ({
  title,
  icon,
  children,
}: {
  title: string;
  icon: ReactNode;
  children: ReactNode[];
}) => {
  if (children.length === 0) return null;
  return (
    <section>
      <h3 className="mb-1 flex items-center gap-1.5 text-[12px] font-semibold uppercase tracking-wide text-ink-subtle">
        {icon}
        {title}
      </h3>
      <ul className="flex flex-col">{children}</ul>
    </section>
  );
};

const Hit = ({
  entityKey,
  title,
  subtitle,
  onSelect,
}: {
  entityKey?: string;
  title: string;
  subtitle: string | null;
  onSelect: () => void;
}) => (
  <li>
    <button
      type="button"
      onClick={onSelect}
      className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left transition-colors hover:bg-sunken"
    >
      {entityKey ? <EntityKey className="w-24 shrink-0">{entityKey}</EntityKey> : null}
      <span className="min-w-0 flex-1 truncate text-[13px] text-ink">{title}</span>
      {subtitle ? (
        <span className="shrink-0 text-[12px] text-ink-subtle">{subtitle}</span>
      ) : null}
    </button>
  </li>
);
